/* ==========================================================================
   THEME — core behaviours as vanilla Custom Elements.
   Progressive enhancement: the storefront is fully usable without this file;
   JS only enriches. Respects prefers-reduced-motion.
   ========================================================================== */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Focus utilities ---------------------------------------------- */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function trapFocus(container, opener) {
  const focusables = () => Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
  const first = focusables()[0];
  if (first) first.focus();
  container._opener = opener;
  container._keydown = (e) => {
    if (e.key !== 'Tab') return;
    const list = focusables();
    if (!list.length) return;
    const firstEl = list[0];
    const lastEl = list[list.length - 1];
    if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
    else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
  };
  container.addEventListener('keydown', container._keydown);
}
function removeTrapFocus(container) {
  if (container && container._keydown) container.removeEventListener('keydown', container._keydown);
  if (container && container._opener && container._opener.focus) container._opener.focus();
}
function lockScroll(on) { document.documentElement.classList.toggle('no-scroll', on); document.body.classList.toggle('no-scroll', on); }

/* ---------- Drawer open/close cascade ------------------------------------
   `.nv-appear` rows ride in on a per-index delay while the panel is open.
   On the way out the panel is already travelling, so the rows are reset
   instantly half a second later (data-closing) instead of being watched
   sliding back down. */
function settleDrawer(drawer) {
  if (!drawer) return;
  drawer.dataset.closing = 'true';
  clearTimeout(drawer._settle);
  const finish = () => {
    drawer.hidden = true;
    delete drawer.dataset.closing;
    drawer.removeEventListener('transitionend', onEnd);
  };
  /* Only the sliding panel counts. The rows inside also fire transitionend,
     and theirs land immediately on close, which would hide the drawer mid-slide. */
  const panel = drawer.querySelector('.mdr__panel, .cart-drawer__panel');
  const onEnd = (e) => { if (e.target === panel && e.propertyName === 'transform') finish(); };
  if (REDUCED_MOTION || !panel) { finish(); return; }
  drawer.addEventListener('transitionend', onEnd);
  /* transitionend never fires if the panel was already off-screen. */
  drawer._settle = setTimeout(finish, 900);
}

/* Re-opening mid-close must cancel the pending hide and the instant-reset flag. */
function wakeDrawer(drawer) {
  if (!drawer) return;
  clearTimeout(drawer._settle);
  delete drawer.dataset.closing;
}

/* ---------- Cart API helpers --------------------------------------------- */
const CartAPI = {
  async add(formData) {
    const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST', headers: { Accept: 'application/json' }, body: formData,
    });
    if (!res.ok) throw await res.json();
    return res.json();
  },
  async get() {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  },
  async change(body) {
    const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
    });
    return res.json();
  },
};
window.Shopify = window.Shopify || {};
window.Shopify.routes = window.Shopify.routes || { root: '/' };

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = false; // count bubble stays visible at 0, matching the reference header
  });
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
}

/* ---------- Sticky header ------------------------------------------------ */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.transparent = this.classList.contains('header-wrapper--transparent');
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }
  disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  onScroll() {
    const y = window.scrollY;
    if (this.transparent) this.dataset.scrolled = y > 40 ? 'true' : 'false';
    /* The reference bar never retracts — it is pinned by CSS and simply stays.
       All this flag does is let a section style the stuck state if it wants. */
    this.dataset.stuck = y > 1 ? 'true' : 'false';
  }
}
customElements.define('site-header', StickyHeader);

/* ---------- Mobile menu drawer ------------------------------------------- */
class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-menu-drawer]');
    this.toggle = document.querySelector('[data-menu-toggle]');
    if (this.toggle) this.toggle.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-menu-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
  }
  open() {
    wakeDrawer(this.drawer);
    this.drawer.hidden = false;
    requestAnimationFrame(() => { this.drawer.dataset.open = 'true'; });
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
    lockScroll(true);
    document.addEventListener('keydown', this.onKey);
    trapFocus(this.drawer, this.toggle);
  }
  close() {
    this.drawer.dataset.open = 'false';
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
    lockScroll(false);
    document.removeEventListener('keydown', this.onKey);
    removeTrapFocus(this.drawer);
    settleDrawer(this.drawer);
  }
}
customElements.define('menu-drawer', MenuDrawer);

/* ---------- Cart drawer -------------------------------------------------- */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-cart-drawer]');
    this.isDrawer = document.body.classList.contains('cart-type-drawer');
    document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => { if (this.isDrawer) { e.preventDefault(); this.open(); } });
    });
    this.querySelectorAll('[data-cart-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
    this.addEventListener('click', (e) => this.onLineAction(e));
    this.addEventListener('click', (e) => this.onAddon(e));
    document.addEventListener('cart:open', () => this.open());
  }
  async open() {
    if (!this.drawer) return;
    wakeDrawer(this.drawer);
    await this.refresh();
    this.drawer.hidden = false;
    requestAnimationFrame(() => { this.drawer.dataset.open = 'true'; });
    lockScroll(true);
    document.addEventListener('keydown', this.onKey);
    trapFocus(this.drawer, document.querySelector('[data-cart-toggle]'));
  }
  close() {
    if (!this.drawer) return;
    this.drawer.dataset.open = 'false';
    lockScroll(false);
    document.removeEventListener('keydown', this.onKey);
    removeTrapFocus(this.drawer);
    settleDrawer(this.drawer);
  }
  async refresh() {
    try {
      /* Inside a section group the id is qualified (sections--123__cart-drawer);
         asking for the bare key renders the section with no blocks. */
      const id = this.dataset.sectionId || 'cart-drawer';
      const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(id)}`);
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const fresh = html.querySelector('[data-cart-contents]');
      const current = this.querySelector('[data-cart-contents]');
      /* A quantity change re-renders the whole body. If the drawer is already
         open the rows must not replay their entrance, or every "+" would make
         the cart jump 60px and fade. The cascade is for the opening only. */
      if (fresh && this.drawer && this.drawer.dataset.open === 'true') {
        fresh.querySelectorAll('.nv-appear').forEach((el) => el.classList.remove('nv-appear'));
      }
      if (fresh && current) current.replaceWith(fresh);
      /* The header sits outside the swapped region, so its count would
         otherwise keep whatever the page was served with. */
      const freshCount = html.querySelector('[data-cart-count-label]');
      const curCount = this.querySelector('[data-cart-count-label]');
      if (freshCount && curCount) curCount.replaceWith(freshCount);
      else if (freshCount && !curCount) {
        const head = this.querySelector('.cdr__head-l');
        if (head) head.appendChild(freshCount);
      } else if (!freshCount && curCount) curCount.remove();
    } catch (e) { /* keep existing markup on failure */ }
  }
  /* The "+" on an add-on card: add, refresh, leave the drawer open. */
  async onAddon(e) {
    const btn = e.target.closest('[data-cart-add]');
    if (!btn) return;
    e.preventDefault();
    btn.setAttribute('aria-busy', 'true');
    try {
      const body = new FormData();
      body.append('id', btn.dataset.variant);
      body.append('quantity', '1');
      await CartAPI.add(body);
      const cart = await CartAPI.get();
      updateCartCount(cart.item_count);
      await this.refresh();
    } catch (err) {
      btn.removeAttribute('aria-busy');
    }
  }

  async onLineAction(e) {
    const btn = e.target.closest('[data-line-change]');
    if (!btn) return;
    e.preventDefault();
    const line = Number(btn.dataset.line);
    const qty = Number(btn.dataset.quantity);
    const cart = await CartAPI.change({ line, quantity: qty });
    updateCartCount(cart.item_count);
    await this.refresh();
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------- Product form (add to cart) ----------------------------------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.button = this.querySelector('[type="submit"]');
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
  }
  async onSubmit(e) {
    if (document.body.classList.contains('cart-type-page') && !this.hasAttribute('data-drawer')) return;
    e.preventDefault();
    if (this.button) { this.button.setAttribute('aria-busy', 'true'); this.button.disabled = true; }
    try {
      const formData = new FormData(this.form);
      const item = await CartAPI.add(formData);
      const cart = await CartAPI.get();
      updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (err) {
      /* Shopify's own description is already localised; the fallback used to be
         the bare word "Error", which a French or German shopper read as-is. */
      const msg = this.querySelector('[data-form-error]');
      if (msg) msg.textContent = (err && err.description) || msg.dataset.fallback || '';
    } finally {
      if (this.button) { this.button.removeAttribute('aria-busy'); this.button.disabled = false; }
    }
  }
}
customElements.define('product-form', ProductForm);

/* ---------- Quantity input ----------------------------------------------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    /* The cart drawer reuses this tag around a read-only count whose buttons
       are handled by CartDrawer's delegated listener. Without this guard every
       click there threw on `this.input.min` before the real handler ran. */
    if (!this.input) return;
    this.querySelectorAll('button').forEach((b) => b.addEventListener('click', (e) => {
      e.preventDefault();
      const step = b.dataset.qtyChange === 'increase' ? 1 : -1;
      const min = Number(this.input.min || 1);
      this.input.value = Math.max(min, (Number(this.input.value) || min) + step);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }));
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- Variant picker ----------------------------------------------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.section = this.dataset.section;
    this.productUrl = this.dataset.url;
    this.addEventListener('change', () => this.onChange());
    try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); } catch (e) { this.variants = []; }
  }
  selectedOptions() {
    return Array.from(this.querySelectorAll('select, input[type="radio"]:checked')).map((el) => el.value);
  }
  currentVariant() {
    const opts = this.selectedOptions();
    return this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
  }
  async onChange() {
    const variant = this.currentVariant();
    const idInput = document.querySelector(`#product-form-${this.section} [name="id"]`);
    if (variant && idInput) idInput.value = variant.id;
    this.updateLabels();
    if (!variant) { this.toggleUnavailable(true); return; }
    this.toggleUnavailable(false);
    const url = new URL(this.productUrl, window.location.origin);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
    try {
      const res = await fetch(`${this.productUrl}?variant=${variant.id}&section_id=${this.section}`);
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      ['[data-price]', '[data-buy-buttons]', '[data-sku]'].forEach((sel) => {
        const fresh = html.querySelector(sel); const cur = document.querySelector(sel);
        if (fresh && cur) cur.replaceWith(fresh);
      });
    } catch (e) { /* noop */ }
    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
  }
  updateLabels() {
    this.querySelectorAll('[data-option-selected]').forEach((el) => {
      const wrap = el.closest('[data-option]');
      const checked = wrap && wrap.querySelector('input[type="radio"]:checked');
      if (checked) el.textContent = checked.value;
    });
  }
  /* Only the label is rewritten. Writing textContent on the button flattened
     it, taking the price span with it: every variant change blanked the price
     until the section fetch answered, and left it blank for good if that fetch
     failed. The price itself is hidden rather than removed while the variant
     cannot be bought. */
  toggleUnavailable(state) {
    const btn = document.querySelector('[data-add-button]');
    if (!btn) return;
    btn.disabled = state;
    const label = btn.querySelector('[data-add-label]');
    const text = state ? btn.dataset.unavailable : btn.dataset.default;
    if (label) label.textContent = text; else btn.textContent = text;
    btn.querySelectorAll('[data-add-price], .buy-buttons__sep').forEach((el) => { el.hidden = state; });
  }
}
customElements.define('variant-picker', VariantPicker);

/* ---------- Sticky add-to-cart ------------------------------------------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const anchor = document.querySelector('[data-atc-anchor]');
    if (anchor && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([entry]) => {
        this.dataset.visible = entry.isIntersecting ? 'false' : 'true';
        if (entry.isIntersecting) this.closeTray();
      }, { rootMargin: '0px 0px -60% 0px' });
      /* Closing the tray when the final CTA takes over keeps only one open. */
      new MutationObserver(() => { if (this.dataset.suppressed === 'true') this.closeTray(); })
        .observe(this, { attributes: true, attributeFilter: ['data-suppressed'] });
      io.observe(anchor);
    }

    this.tray = this.querySelector('[data-satc-tray]');
    this.toggle = this.querySelector('[data-satc-toggle]');

    if (this.toggle && this.tray) {
      this.toggle.addEventListener('click', () => {
        this.tray.hidden ? this.openTray() : this.closeTray();
      });
      this.querySelectorAll('[data-satc-close]').forEach((b) => b.addEventListener('click', () => this.closeTray()));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeTray(); });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeTray(); });
    }

    /* Picking a chip drives the real variant radio so every existing
       behaviour (price, availability, gallery, URL) keeps working. */
    this.querySelectorAll('[data-satc-pick]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const value = chip.dataset.satcPick;
        const pos = chip.dataset.satcPos;
        const input = document.querySelector(
          `variant-picker input[name="option${pos}"][value="${window.CSS && CSS.escape ? CSS.escape(value) : value}"]`
        ) || Array.from(document.querySelectorAll(`variant-picker input[name="option${pos}"]`))
              .find((i) => i.value === value);
        if (!input) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncColour(value, chip);
        this.closeTray();
      });
    });

    /* Mirror the main add button so lots and line item properties are kept. */
    const add = this.querySelector('[data-satc-add]');
    if (add) {
      add.addEventListener('click', () => {
        const main = document.querySelector('[data-add-button]');
        if (main) main.click();
      });
    }

    document.addEventListener('variant:change', () => this.syncPrice());
    document.addEventListener('lot:change', () => this.syncPrice());
    this.syncPrice();
  }

  openTray() {
    if (!this.tray) return;
    this.tray.hidden = false;
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
    const on = this.tray.querySelector('.satc__chip.is-on');
    if (on) on.scrollIntoView({ block: 'nearest' });
  }

  closeTray() {
    if (!this.tray || this.tray.hidden) return;
    this.tray.hidden = true;
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
  }

  syncColour(value, chip) {
    this.querySelectorAll('[data-satc-pick]').forEach((c) => c.classList.toggle('is-on', c === chip));
    const label = this.querySelector('[data-satc-value]');
    const dot = this.querySelector('[data-satc-chip]');
    if (label) label.textContent = value;
    if (dot && chip) dot.style.cssText = chip.style.cssText;
  }

  syncPrice() {
    const target = this.querySelector('[data-satc-price]');
    if (!target) return;
    /* The bar mirrors the main button, so it must quote what that button
       charges: the lot total when one is selected, not the unit price the
       variant price element shows. */
    const lot = document.querySelector('[data-bundle-qty]:checked, [data-lot-value]:checked');
    const total = lot ? Number(lot.dataset.bundleTotal) : NaN;
    if (Number.isFinite(total) && total > 0) {
      target.textContent = formatMoney(total);
      return;
    }
    const fresh = document.querySelector('[data-price] .price');
    if (fresh) target.innerHTML = fresh.outerHTML;
  }
}
customElements.define('sticky-atc', StickyAtc);

/* ---------- Scroll reveal ------------------------------------------------ */
function initReveal() {
  const els = document.querySelectorAll('[data-rv], [reveal]');
  if (!els.length) return;
  if (REDUCED_MOTION || !('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-in', 'is-visible'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-in', 'is-visible'); io.unobserve(entry.target); }
    });
  }, { rootMargin: '0px 0px -5% 0px', threshold: 0 });
  els.forEach((el) => io.observe(el));
  // Failsafe: never leave content hidden if the observer never fires for an element.
  setTimeout(() => els.forEach((el) => { if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-in', 'is-visible'); }), 1200);
}

/* ---------- Before / after slider ---------------------------------------- */
class BeforeAfter extends HTMLElement {
  connectedCallback() {
    this.frame = this.querySelector('.ba__frame');
    this.before = this.querySelector('[data-ba-before]');
    this.handle = this.querySelector('[data-ba-handle]');
    if (!this.frame || !this.before || !this.handle) return;
    this.pct = 50;
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.handle.addEventListener('pointerdown', (e) => this.start(e));
    this.frame.addEventListener('pointerdown', (e) => { if (!e.target.closest('.ba__handle')) { this.moveTo(e.clientX); this.start(e); } });
    this.handle.addEventListener('keydown', (e) => this.onKey(e));
    this.set(50);
  }
  start(e) {
    e.preventDefault();
    this.dragging = true;
    try { this.handle.setPointerCapture(e.pointerId); } catch (_) {}
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp, { once: true });
  }
  onUp() { this.dragging = false; window.removeEventListener('pointermove', this.onMove); }
  onMove(e) { if (this.dragging) this.moveTo(e.clientX); }
  moveTo(clientX) {
    const rect = this.frame.getBoundingClientRect();
    this.set(((clientX - rect.left) / rect.width) * 100);
  }
  set(pct) {
    this.pct = Math.max(0, Math.min(100, pct));
    this.before.style.clipPath = `inset(0 ${100 - this.pct}% 0 0)`;
    this.handle.style.left = `${this.pct}%`;
    this.handle.setAttribute('aria-valuenow', Math.round(this.pct));
  }
  onKey(e) {
    const step = e.shiftKey ? 10 : 3;
    if (e.key === 'ArrowLeft') { this.set(this.pct - step); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { this.set(this.pct + step); e.preventDefault(); }
  }
}
customElements.define('before-after', BeforeAfter);

/* reveal-text uses the shared [reveal] system (see initReveal) for its is-visible
   toggle; the per-word stagger comes from CSS transition-delay. */

/* ---------- Auto-submit selects (localization) --------------------------- */
function initAutoSubmit() {
  document.querySelectorAll('[data-auto-submit]').forEach((sel) => {
    sel.addEventListener('change', () => sel.closest('form') && sel.closest('form').submit());
  });
}

/* ---------- Product gallery ---------------------------------------------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-gallery-stage]');
    this.slides = Array.from(this.querySelectorAll('.pg__slide'));
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.select(t.dataset.target)));
    this.desktop = window.matchMedia('(min-width: 821px)');
    this.firstId = this.slides.length ? this.slides[0].dataset.mediaId : null;
    this.watchScroll();
  }
  /* The badge is pinned over the whole stage, so it would sit on top of every
     slide as you swipe. It belongs to the opening shot only. */
  syncBadge(id) {
    this.classList.toggle('pg--past-first', this.firstId !== null && id !== this.firstId);
  }
  select(id) {
    this.syncBadge(id);
    this.slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === id));
    this.thumbs.forEach((t) => {
      const on = t.dataset.target === id;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (!this.desktop.matches) {
      const target = this.querySelector(`#Slide-${id}`);
      if (target) target.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
    }
  }
  /* On mobile the stage is a snap scroller, so the thumbs follow the swipe. */
  watchScroll() {
    if (!this.stage || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      if (this.desktop.matches) return;
      const seen = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!seen) return;
      const id = seen.target.dataset.mediaId;
      this.syncBadge(id);
      this.thumbs.forEach((t) => {
        const on = t.dataset.target === id;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }, { root: this.stage, threshold: 0.6 });
    this.slides.forEach((s) => io.observe(s));
  }
}
customElements.define('product-gallery', ProductGallery);

/* ---------- Product recommendations (async) ------------------------------ */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('.product-card, .card')) return;
    const url = this.dataset.url;
    if (!url) return;
    fetch(url)
      .then((r) => r.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const fresh = html.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        initReveal();
      })
      .catch(() => {});
  }
}
customElements.define('product-recommendations', ProductRecommendations);


/* ---------- Share + sticky-atc button ------------------------------------ */
function initProductExtras() {
  document.querySelectorAll('[data-share]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const shareData = { title: document.title, url: window.location.href };
      if (navigator.share) { try { await navigator.share(shareData); } catch (e) {} }
      else if (navigator.clipboard) { await navigator.clipboard.writeText(window.location.href); btn.setAttribute('data-copied', 'true'); }
    });
  });
  document.querySelectorAll('[data-cart-toggle-scroll]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const add = document.querySelector('[data-add-button]');
      if (add && !add.disabled) { e.preventDefault(); add.click(); }
    });
  });
}

/* ---------- Predictive search -------------------------------------------- */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.dataset.predictive !== 'true') return;
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-predictive-results]');
    if (!this.input || !this.results) return;
    this.timer = null;
    this.input.addEventListener('input', () => this.onInput());
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.hide(); });
    this.input.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.hide(); });
  }
  onInput() {
    clearTimeout(this.timer);
    const q = this.input.value.trim();
    if (q.length < 2) { this.hide(); return; }
    this.timer = setTimeout(() => this.search(q), 250);
  }
  async search(q) {
    const root = window.Shopify.routes.root;
    const url = `${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,page,article&resources[limit]=6&resources[options][unavailable_products]=last`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      this.render(data.resources.results, q);
    } catch (e) { this.hide(); }
  }
  render(r, q) {
    let html = '';
    if (r.products && r.products.length) {
      html += `<p class="predictive-group__title">${window.themeStrings ? window.themeStrings.products : 'Products'}</p>`;
      html += r.products.map((p) => `<a class="predictive-item" href="${p.url}">${p.featured_image && p.featured_image.url ? `<img class="predictive-item__img" src="${p.featured_image.url}&width=88" alt="" loading="lazy">` : ''}<span>${p.title}</span></a>`).join('');
    }
    ['pages', 'articles'].forEach((type) => {
      if (r[type] && r[type].length) {
        html += `<p class="predictive-group__title">${type}</p>`;
        html += r[type].map((i) => `<a class="predictive-item" href="${i.url}"><span>${i.title}</span></a>`).join('');
      }
    });
    if (!html) html = `<p class="predictive-group__title">${'No results'}</p>`;
    this.results.innerHTML = html;
    this.results.hidden = false;
  }
  hide() { if (this.results) { this.results.hidden = true; } }
}
customElements.define('predictive-search', PredictiveSearch);

/* ---------- Collection facets (mobile drawer) ---------------------------- */
function initFacets() {
  const facets = document.querySelector('[data-facets]');
  if (!facets) return;
  const toggle = document.querySelector('[data-facets-toggle]');
  const open = () => { facets.dataset.open = 'true'; lockScroll(true); if (toggle) toggle.setAttribute('aria-expanded', 'true'); };
  const close = () => { facets.dataset.open = 'false'; lockScroll(false); if (toggle) toggle.setAttribute('aria-expanded', 'false'); };
  if (toggle) toggle.addEventListener('click', open);
  facets.querySelectorAll('[data-facets-close]').forEach((b) => b.addEventListener('click', close));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && facets.dataset.open === 'true') close(); });
}




/* Keep the button price in step with the selected variant when there is no lot. */
function syncButtonPrice() {
  const update = () => {
    const priceEl = document.querySelector('[data-add-price]');
    const qtyInput = document.querySelector('.buy-buttons input[name="quantity"]');
    if (!priceEl || !qtyInput) return;
    const unit = Number(priceEl.dataset.unit);
    if (!Number.isFinite(unit)) return;
    priceEl.textContent = formatMoney(unit * Math.max(1, Number(qtyInput.value) || 1));
  };
  /* Delegated rather than bound to the inputs: a variant change replaces the
     whole buy card, so captured nodes go stale and the button kept quoting one
     unit however many the shopper asked for. Packs being variants made that a
     one-click trip. */
  document.addEventListener('change', (e) => {
    if (e.target instanceof Element && e.target.matches('.buy-buttons input[name="quantity"]')) update();
  });
  document.addEventListener('variant:change', () => setTimeout(update, 80));
}


/* ---------- "Complete the routine" bundle ------------------------------- */
class RoutineBundle extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-routine-item]'));
    this.totalEl = this.querySelector('[data-routine-total]');
    this.wasEl = this.querySelector('[data-routine-was]');
    this.btn = this.querySelector('[data-routine-add]');
    this.err = this.querySelector('[data-routine-error]');
    if (!this.items.length || !this.btn) return;
    this.discount = Number(this.btn.dataset.discount || 0);
    this.items.forEach((i) => i.addEventListener('change', () => this.recalc()));
    this.btn.addEventListener('click', () => this.add());
    this.recalc();
  }
  selected() { return this.items.filter((i) => i.checked && !i.disabled); }
  recalc() {
    const sel = this.selected();
    const raw = sel.reduce((n, i) => n + Number(i.dataset.price || 0), 0);
    const net = Math.round(raw - (raw * this.discount) / 100);
    if (this.totalEl) this.totalEl.textContent = formatMoney(net);
    if (this.wasEl) { this.wasEl.textContent = formatMoney(raw); this.wasEl.hidden = this.discount === 0 || raw === 0; }
    this.btn.disabled = sel.length === 0;
  }
  async add() {
    const sel = this.selected();
    if (!sel.length) return;
    this.btn.disabled = true;
    this.btn.setAttribute('aria-busy', 'true');
    try {
      const items = sel.map((i) => ({ id: Number(i.value), quantity: 1 }));
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw await res.json();
      const cart = await CartAPI.get();
      updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:open'));
      if (this.err) this.err.textContent = '';
    } catch (e) {
      if (this.err) this.err.textContent = (e && e.description) || 'Error';
    } finally {
      this.btn.removeAttribute('aria-busy');
      this.recalc();
    }
  }
}
customElements.define('routine-bundle', RoutineBundle);

/* Money formatting mirroring the shop's format string. */
function formatMoney(cents) {
  const fmt = (window.Shopify && window.Shopify.moneyFormat) || '${{amount}}';
  const amount = (cents / 100).toFixed(2);
  const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fmt.replace(/\{\{\s*amount[^}]*\}\}/, withCommas);
}


/* ---------- Testimonial slider (auto-advance + dots) --------------------- */
class TestimonialSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-tslider]');
    this.dots = this.querySelector('[data-tdots]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    if (this.slides.length < 2) return;
    this.index = 0;
    this.buildDots();
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    if (this.dataset.autoplay === 'true' && !REDUCED_MOTION) {
      const ms = (Number(this.dataset.interval) || 6) * 1000;
      const start = () => { this.timer = setInterval(() => this.go(this.index + 1), ms); };
      const stop = () => clearInterval(this.timer);
      start();
      this.addEventListener('pointerenter', stop);
      this.addEventListener('pointerleave', start);
      this.addEventListener('focusin', stop);
    }
  }
  buildDots() {
    if (!this.dots) return;
    this.dots.innerHTML = '';
    this.slides.forEach((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', `${i + 1}`);
      b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      b.addEventListener('click', () => this.go(i));
      this.dots.appendChild(b);
    });
  }
  go(i) {
    const n = this.slides.length;
    this.index = ((i % n) + n) % n;
    const target = this.slides[this.index];
    this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    this.sync();
  }
  onScroll() {
    const mid = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0, dist = Infinity;
    this.slides.forEach((s, i) => {
      const c = s.offsetLeft - this.track.offsetLeft + s.clientWidth / 2;
      if (Math.abs(c - mid) < dist) { dist = Math.abs(c - mid); best = i; }
    });
    this.index = best;
    this.sync();
  }
  sync() {
    if (!this.dots) return;
    Array.from(this.dots.children).forEach((d, i) => d.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
  }
}
customElements.define('testimonial-slider', TestimonialSlider);

/* ---------- Boot --------------------------------------------------------- */


/* ---------- Lazy background video ---------------------------------------- */
function initBgVideo() {
  const vids = document.querySelectorAll('[data-stmt-video]');
  if (!vids.length) return;
  /* Reduced motion keeps the poster and never fetches the file. */
  if (REDUCED_MOTION || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const v = entry.target;
      if (!entry.isIntersecting) { if (!v.paused) v.pause(); return; }
      if (!v.src && v.dataset.src) { v.src = v.dataset.src; v.load(); }
      const play = v.play();
      if (play && play.catch) play.catch(() => {});
    });
  }, { rootMargin: '200px 0px' });
  vids.forEach((v) => io.observe(v));
}


/* Only fade the spec strip when it actually has more to show. */

function boot() { initReveal(); initAutoSubmit(); initProductExtras(); initFacets(); initBgVideo(); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', boot);

/* Shared with product.js, which only loads on product templates. A namespace
   rather than an ESM export: a relative import would resolve to a different
   URL than the versioned asset_url and load a second module instance, which
   would re-run customElements.define and throw. */
window.theme = Object.assign(window.theme || {}, { formatMoney, syncButtonPrice, REDUCED_MOTION });
